import { useCallback, useEffect, useState } from "react";

export interface AuthUser {
  email: string;
  name?: string;
  picture?: string;
}

interface MeResponse {
  authenticated: boolean;
  email?: string;
  name?: string;
  picture?: string;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me", { credentials: "same-origin" });
      if (!res.ok) throw new Error(`status=${res.status}`);
      const data = (await res.json()) as MeResponse;
      if (data.authenticated && data.email) {
        setUser({ email: data.email, name: data.name, picture: data.picture });
      } else {
        setUser(null);
      }
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const signIn = useCallback(
    async (credential: string): Promise<{ ok: boolean; error?: string }> => {
      const res = await fetch("/api/auth/verify", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        email?: string;
        name?: string;
        picture?: string;
        error?: string;
      };
      if (!res.ok || !data.ok) {
        return { ok: false, error: data.error || `status=${res.status}` };
      }
      setUser({ email: data.email!, name: data.name, picture: data.picture });
      return { ok: true };
    },
    [],
  );

  const signOut = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
    setUser(null);
  }, []);

  return { user, loading, error, signIn, signOut, refetch: fetchMe };
}
